import { z } from 'zod';
import { SaleSchema } from '@am3nd-org/pointcraft-types';

import {
  hasQuickPayLine,
  type EditableSaleDraft,
  createEditableSaleSnapshot,
} from '../sales/editable-sale-snapshot';
import {
  ACTIVE_SALE_DRAFT_DB_PREFIX,
  saveActiveSaleDraftRecordToDB,
  getActiveSaleDraftRecordFromDB,
} from './DB_Utils';

export const ACTIVE_SALE_DRAFT_VERSION = 1 as const;

export interface ActiveSaleDraftScope {
  environment: string;
  businessId: string;
  businessDeviceId: string | null;
  managerUserId?: string;
  installationId?: string;
  businessLocationId: string;
}

interface ActiveSaleDraftRecordBase {
  version: typeof ACTIVE_SALE_DRAFT_VERSION;
  scope: ActiveSaleDraftScope;
  revision: number;
  updatedAt: string;
}

export interface ActiveSaleRecord extends ActiveSaleDraftRecordBase {
  kind: 'active';
  draft: EditableSaleDraft;
}

export interface ClearedSaleRecord extends ActiveSaleDraftRecordBase {
  kind: 'cleared';
  paymentExitId?: string;
}

export type ActiveSaleDraftRecord = ActiveSaleRecord | ClearedSaleRecord;

export type ActiveSalePersistenceFailure =
  | 'database-read'
  | 'database-write'
  | 'mirror-read'
  | 'mirror-write'
  | 'invalid-record';

export interface ActiveSaleDraftLoadResult {
  record: ActiveSaleDraftRecord | null;
  hadInvalidRecord: boolean;
  hadStorageFailure: boolean;
}

const ScopeSchema = z.object({
  environment: z.string().min(1),
  businessId: z.string().min(1),
  businessDeviceId: z.string().min(1).nullable(),
  managerUserId: z.string().min(1).optional(),
  installationId: z.string().min(1).optional(),
  businessLocationId: z.string().min(1),
});

const EditableSaleOverridesSchema = z.object({
  removeFeesOverride: z.boolean(),
  taxEnabledOverride: z.boolean().nullable(),
  clientPaysFeesOverride: z.boolean().nullable(),
});

const RecordBaseSchema = z.object({
  version: z.literal(ACTIVE_SALE_DRAFT_VERSION),
  scope: ScopeSchema,
  revision: z.number().int().nonnegative(),
  updatedAt: z.string().datetime(),
});

const RawRecordSchema = z.discriminatedUnion('kind', [
  RecordBaseSchema.extend({
    kind: z.literal('active'),
    draft: z.object({
      sale: z.unknown(),
      overrides: EditableSaleOverridesSchema,
    }),
  }),
  RecordBaseSchema.extend({ kind: z.literal('cleared'), paymentExitId: z.string().optional() }),
]);

type PendingDatabaseWrite = {
  key: string;
  record: ActiveSaleDraftRecord;
};

const lastRevisionByKey = new Map<string, number>();
const failureListeners = new Set<(failure: ActiveSalePersistenceFailure) => void>();
const pendingDatabaseWrites = new Map<string, PendingDatabaseWrite>();
let databaseWriter: Promise<void> | null = null;
let suspendedForLogout = false;
let runtimeEnabled = false;

function reportFailure(failure: ActiveSalePersistenceFailure, error?: unknown): void {
  const errorCategory = error instanceof Error ? error.name : null;
  console.warn(`[ActiveSalePersistence] ${failure}`, errorCategory ? { errorCategory } : '');
  failureListeners.forEach((listener) => listener(failure));
}

function scopesMatch(left: ActiveSaleDraftScope, right: ActiveSaleDraftScope): boolean {
  return (
    left.environment === right.environment &&
    left.businessId === right.businessId &&
    left.businessDeviceId === right.businessDeviceId &&
    left.managerUserId === right.managerUserId &&
    left.installationId === right.installationId &&
    left.businessLocationId === right.businessLocationId
  );
}

function nextRevision(key: string, scope: ActiveSaleDraftScope): number {
  const mirrorRevision = getMirrorRecord(key, scope).record?.revision ?? 0;
  const next = Math.max(Date.now(), (lastRevisionByKey.get(key) ?? 0) + 1, mirrorRevision + 1);
  lastRevisionByKey.set(key, next);
  return next;
}

function parseRecord(
  value: unknown,
  expectedScope: ActiveSaleDraftScope
): { record: ActiveSaleDraftRecord | null; invalid: boolean } {
  if (value === null || value === undefined) return { record: null, invalid: false };

  const raw = RawRecordSchema.safeParse(value);
  if (!raw.success || !scopesMatch(raw.data.scope, expectedScope)) {
    return { record: null, invalid: true };
  }

  if (raw.data.kind === 'cleared') {
    return { record: raw.data, invalid: false };
  }

  const parsedSale = SaleSchema.safeParse(raw.data.draft.sale);
  if (
    !parsedSale.success ||
    parsedSale.data.items.length === 0 ||
    hasQuickPayLine(parsedSale.data)
  ) {
    return { record: null, invalid: true };
  }

  return {
    record: {
      ...raw.data,
      draft: {
        sale: createEditableSaleSnapshot(parsedSale.data),
        overrides: raw.data.draft.overrides,
      },
    },
    invalid: false,
  };
}

function getMirrorRecord(
  key: string,
  scope: ActiveSaleDraftScope
): { record: ActiveSaleDraftRecord | null; invalid: boolean; failed: boolean } {
  if (typeof localStorage === 'undefined') return { record: null, invalid: false, failed: false };

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { record: null, invalid: false, failed: false };
    const parsed = parseRecord(JSON.parse(raw), scope);
    if (parsed.invalid) localStorage.removeItem(key);
    return { ...parsed, failed: false };
  } catch (error) {
    reportFailure('mirror-read', error);
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing else can safely be done with an inaccessible mirror.
    }
    return { record: null, invalid: true, failed: true };
  }
}

function writeMirrorRecord(key: string, record: ActiveSaleDraftRecord): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    localStorage.setItem(key, JSON.stringify(record));
    return true;
  } catch (error) {
    reportFailure('mirror-write', error);
    return false;
  }
}

function startDatabaseWriter(): void {
  if (databaseWriter || suspendedForLogout || pendingDatabaseWrites.size === 0) return;

  databaseWriter = (async () => {
    while (pendingDatabaseWrites.size > 0 && !suspendedForLogout) {
      const write = pendingDatabaseWrites.values().next().value as PendingDatabaseWrite;
      pendingDatabaseWrites.delete(write.key);
      try {
        // eslint-disable-next-line no-await-in-loop -- one serialized writer prevents stale completion order
        await saveActiveSaleDraftRecordToDB(write.key, write.record);
      } catch (error) {
        reportFailure('database-write', error);
      }
    }
  })().finally(() => {
    databaseWriter = null;
    if (pendingDatabaseWrites.size > 0 && !suspendedForLogout) startDatabaseWriter();
  });
}

function scheduleDatabaseWrite(key: string, record: ActiveSaleDraftRecord): void {
  if (suspendedForLogout) return;
  pendingDatabaseWrites.set(key, { key, record });
  startDatabaseWriter();
}

function writeRecord(key: string, record: ActiveSaleDraftRecord): ActiveSaleDraftRecord | null {
  if (suspendedForLogout) return null;
  lastRevisionByKey.set(key, Math.max(lastRevisionByKey.get(key) ?? 0, record.revision));
  writeMirrorRecord(key, record);
  scheduleDatabaseWrite(key, record);
  return record;
}

function chooseNewestRecord(
  databaseRecord: ActiveSaleDraftRecord | null,
  mirrorRecord: ActiveSaleDraftRecord | null
): ActiveSaleDraftRecord | null {
  if (!databaseRecord) return mirrorRecord;
  if (!mirrorRecord) return databaseRecord;
  if (databaseRecord.revision !== mirrorRecord.revision) {
    return databaseRecord.revision > mirrorRecord.revision ? databaseRecord : mirrorRecord;
  }
  if (databaseRecord.kind !== mirrorRecord.kind) {
    return databaseRecord.kind === 'cleared' ? databaseRecord : mirrorRecord;
  }
  return databaseRecord;
}

export function buildActiveSaleDraftKey(scope: ActiveSaleDraftScope): string {
  ScopeSchema.parse(scope);
  if (!scope.businessDeviceId && (!scope.managerUserId || !scope.installationId)) throw new Error('Payment owner is missing');
  const owner = scope.businessDeviceId ?? `manager:${scope.managerUserId}:${scope.installationId}`;
  return `${ACTIVE_SALE_DRAFT_DB_PREFIX}${scope.environment}:${scope.businessId}:${owner}:${scope.businessLocationId}`;
}

/** Synchronous ownership/revision check for payment-exit write-ahead commits. */
export function readActiveSaleDraftMirror(
  scope: ActiveSaleDraftScope
): ActiveSaleDraftRecord | null {
  return getMirrorRecord(buildActiveSaleDraftKey(scope), scope).record;
}

export async function loadActiveSaleDraft(
  scope: ActiveSaleDraftScope
): Promise<ActiveSaleDraftLoadResult> {
  const key = buildActiveSaleDraftKey(scope);
  const initialMirror = getMirrorRecord(key, scope);
  let database: ReturnType<typeof parseRecord> = { record: null, invalid: false };
  let databaseFailed = false;

  try {
    const raw = await getActiveSaleDraftRecordFromDB<unknown>(key);
    database = parseRecord(raw, scope);
  } catch (error) {
    databaseFailed = true;
    reportFailure('database-read', error);
  }

  if (suspendedForLogout) {
    return { record: null, hadInvalidRecord: false, hadStorageFailure: databaseFailed };
  }

  // Database opening may be slow. Re-read the synchronous mirror after that
  // await so a cart edit/tombstone written during hydration always wins.
  const latestMirror = getMirrorRecord(key, scope);
  const mirror = {
    record: latestMirror.record,
    invalid: initialMirror.invalid || latestMirror.invalid,
    failed: initialMirror.failed || latestMirror.failed,
  };
  const newest = chooseNewestRecord(database.record, mirror.record);
  if (newest) {
    lastRevisionByKey.set(key, newest.revision);
    if (
      !mirror.record ||
      mirror.record.revision !== newest.revision ||
      mirror.record.kind !== newest.kind
    ) {
      writeMirrorRecord(key, newest);
    }
    if (
      !databaseFailed &&
      (!database.record ||
        database.record.revision !== newest.revision ||
        database.record.kind !== newest.kind)
    ) {
      scheduleDatabaseWrite(key, newest);
    }
    return {
      record: newest,
      hadInvalidRecord: database.invalid || mirror.invalid,
      hadStorageFailure: databaseFailed || mirror.failed,
    };
  }

  const hadInvalidRecord = database.invalid || mirror.invalid;
  if (hadInvalidRecord && !databaseFailed) {
    reportFailure('invalid-record');
    const tombstone = commitClearedActiveSale(scope);
    return { record: tombstone, hadInvalidRecord: true, hadStorageFailure: mirror.failed };
  }

  return {
    record: null,
    hadInvalidRecord,
    hadStorageFailure: databaseFailed || mirror.failed,
  };
}

export function commitActiveSaleDraft(
  scope: ActiveSaleDraftScope,
  draft: EditableSaleDraft
): ActiveSaleRecord | null {
  try {
    if (draft.sale.items.length === 0) return null;
    if (hasQuickPayLine(draft.sale)) return null;
    const key = buildActiveSaleDraftKey(scope);
    const record: ActiveSaleRecord = {
      version: ACTIVE_SALE_DRAFT_VERSION,
      kind: 'active',
      scope,
      revision: nextRevision(key, scope),
      updatedAt: new Date().toISOString(),
      draft: {
        sale: createEditableSaleSnapshot(draft.sale),
        overrides: EditableSaleOverridesSchema.parse(draft.overrides),
      },
    };
    return writeRecord(key, record) as ActiveSaleRecord | null;
  } catch {
    reportFailure('invalid-record');
    return null;
  }
}

export function commitClearedActiveSale(
  scope: ActiveSaleDraftScope,
  paymentExitId?: string
): ClearedSaleRecord | null {
  const key = buildActiveSaleDraftKey(scope);
  const record: ClearedSaleRecord = {
    version: ACTIVE_SALE_DRAFT_VERSION,
    kind: 'cleared',
    ...(paymentExitId ? { paymentExitId } : {}),
    scope,
    revision: nextRevision(key, scope),
    updatedAt: new Date().toISOString(),
  };
  return writeRecord(key, record) as ClearedSaleRecord | null;
}

export function setActiveSalePersistenceRuntimeEnabled(enabled: boolean): void {
  runtimeEnabled = enabled && !suspendedForLogout;
}

export function isActiveSalePersistenceRuntimeEnabled(): boolean {
  return runtimeEnabled && !suspendedForLogout;
}

export function resumeActiveSalePersistence(): void {
  suspendedForLogout = false;
  startDatabaseWriter();
}

export async function suspendActiveSalePersistenceForLogout(): Promise<void> {
  runtimeEnabled = false;
  suspendedForLogout = true;
  pendingDatabaseWrites.clear();
  if (databaseWriter) await databaseWriter;
}

export async function flushActiveSalePersistenceWrites(): Promise<void> {
  while (databaseWriter || pendingDatabaseWrites.size > 0) {
    startDatabaseWriter();
    // eslint-disable-next-line no-await-in-loop -- test/cleanup drain must include writes queued by the active writer
    if (databaseWriter) await databaseWriter;
  }
}

export function subscribeActiveSalePersistenceFailures(
  listener: (failure: ActiveSalePersistenceFailure) => void
): () => void {
  failureListeners.add(listener);
  return () => failureListeners.delete(listener);
}

/** Test-only reset for module-level write/revision state. */
export async function resetActiveSalePersistenceForTests(): Promise<void> {
  pendingDatabaseWrites.clear();
  if (databaseWriter) await databaseWriter;
  databaseWriter = null;
  lastRevisionByKey.clear();
  failureListeners.clear();
  suspendedForLogout = false;
  runtimeEnabled = false;
}
